import { Injectable } from '@angular/core';
import {
    Action, createSelector, Selector, State, StateContext
} from '@ngxs/store';
import {
    append, iif, insertItem, patch, removeItem, updateItem
} from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Customer } from '../../services/dtos/customer';
import { CustomerPluginConfiguration } from '../../services/dtos/customerPluginConfiguration';
import { CustomersService } from './../../services/customers.service';
import { CustomerBasicInfo } from './../../services/dtos/customerBasicInfo';

import { CustomerActionConfiguration } from 'src/app/services/dtos/customerActionConfiguration';
import {
  AddCustomer,
  AddCustomerMapping,
  AddOrUpdateCustomerActionConfiguration,
  ClearCurrentCustomerId,
  DeleteCustomer,
  DeleteCustomerActionConfiguration,
  FetchAllCustomersBasicInfo,
  FetchCurrentUserAllCustomersBasicInfo,
  FetchCustomer,
  FetchCustomerActionConfiguration,
  FetchCustomerPluginConfigurationIfEmpty,
  FetchMessageTypesIfEmpty,
  ImportCustomer,
  SetActivePluginId,
  SetCurrentCustomerId,
  SetSelectedMessageType,
  UpdateCustomer,
  UpdatePluginCustomerConfigurationFile
} from './customers.actions';

export class CustomersStateModel {
  public customers!: Customer[];
  public currentUserCustomers!: Customer[];
  public messageTypes!: string[];
  public selectedCustomerId!: string;
  public selectedCustomerActions!: string[];
  public selectedMessageType!: string;
  public activePluginId!: string;
  public customerPluginConfigurations!: CustomerPluginConfiguration[];
  public customerActionConfigurations!: CustomerActionConfiguration[];
  public selectedCustomerMappings!: string[];
}

const defaults = {
  customers: [],
  currentUserCustomers: [],
  messageTypes: [],
  laboratories: [],
  selectedCustomerId: '',
  selectedCustomerActions: [],
  selectedMessageType: '',
  activePluginId: '',
  customerPluginConfigurations: [],
  customerActionConfigurations: [],
  selectedCustomerMappings: [],
};

@State<CustomersStateModel>({
  name: 'customersState',
  defaults,
})
@Injectable()
export class CustomersState {
  public constructor(private customersService: CustomersService) {}

  @Selector()
  public static getCustomersBasicInfo(
    state: CustomersStateModel
  ): CustomerBasicInfo[] {
    return state.customers as CustomerBasicInfo[];
  }

  @Selector()
  public static getCurrentUserCustomersBasicInfo(
    state: CustomersStateModel
  ): CustomerBasicInfo[] {
    return state.currentUserCustomers as CustomerBasicInfo[];
  }

  @Selector()
  public static getSelectedCustomerId(state: CustomersStateModel): string {
    return state.selectedCustomerId;
  }

  @Selector()
  public static getSelectedCustomer(
    state: CustomersStateModel
  ): Customer | undefined {
    return state.customers.find((c) => c.id === state.selectedCustomerId);
  }

  @Selector()
  public static getSelectedCustomerActions(
    state: CustomersStateModel
  ): string[] | undefined {
    return state.selectedCustomerActions;
  }

  @Selector()
  public static getSelectedMessageType(state: CustomersStateModel): string {
    return state.selectedMessageType;
  }

  @Selector()
  public static getActivePluginId(state: CustomersStateModel): string {
    return state.activePluginId;
  }

  @Selector()
  public static getMessageTypes(state: CustomersStateModel): string[] {
    return state.messageTypes;
  }

  @Selector()
  public static getSelectedCustomerMappings(
    state: CustomersStateModel
  ): string[] {
    return state.selectedCustomerMappings;
  }

  // eslint-disable-next-line 
  public static getCustomerPluginConfigurations(pluginId: string) {
    return createSelector([CustomersState], (state: CustomersStateModel) =>
      state.customerPluginConfigurations.find(
        (x) =>
          x.customerId === state.selectedCustomerId && x.pluginId === pluginId
      )
    );
  }

  // eslint-disable-next-line
  public static getCustomerActionConfiguration(actionId: string) {
    return createSelector([CustomersState], (state: CustomersStateModel) =>
      state.customerActionConfigurations.find(
        (x) =>
          x.customerId === state.selectedCustomerId && x.actionId === actionId
      )
    );
  }

  @Action(FetchMessageTypesIfEmpty)
  public fetchAllMessageTypes({
    getState,
    setState,
  }: StateContext<CustomersStateModel>): Observable<string[]> | void {
    if (getState().messageTypes.length === 0) {
      return this.customersService.getAllMessageTypes().pipe(
        tap((messageTypes: string[]) => {
          const state = getState();
          setState({ ...state, messageTypes });
        })
      );
    }
  }

  @Action(FetchCustomerPluginConfigurationIfEmpty)
  public fetchPluginCustomerConfigurationIfEmpty(
    { getState, setState }: StateContext<CustomersStateModel>,
    { pluginId }: FetchCustomerPluginConfigurationIfEmpty
  ): Observable<CustomerPluginConfiguration> | void {
    const currentState = getState();
    if (
      currentState.customerPluginConfigurations.some(
        (x) =>
          x.customerId === currentState.selectedCustomerId &&
          x.pluginId === pluginId
      )
    ) {
      return;
    }

    return this.customersService
      .getCustomerPluginConfiguration(currentState.selectedCustomerId, pluginId)
      .pipe(
        tap((customerPluginConfiguration: CustomerPluginConfiguration) => {
          setState(
            patch<CustomersStateModel>({
              customerPluginConfigurations:
                iif<CustomerPluginConfiguration[]>(
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  (pluginConfiguration) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    !pluginConfiguration!.some(
                      (x) =>
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        x!.pluginId === pluginId && x!.customerId === currentState.selectedCustomerId
                    ),
                insertItem<CustomerPluginConfiguration>(
                  customerPluginConfiguration
                ),
            ),
          })
          );
        })
      );
  }

  @Action(FetchCustomerActionConfiguration)
  public fetchActionCustomerConfiguration(
    { getState, setState }: StateContext<CustomersStateModel>,
    { actionId }: FetchCustomerActionConfiguration
  ): Observable<CustomerActionConfiguration> | void {
    const currentState = getState();
    return this.customersService
      .getCustomerActionConfiguration(currentState.selectedCustomerId, actionId)
      .pipe(
        tap((customerActionConfiguration: CustomerActionConfiguration) => {
          setState(
            patch<CustomersStateModel>({
              customerActionConfigurations:
                insertItem<CustomerActionConfiguration>(
                  customerActionConfiguration
                ),
            })
          );
        })
      );
  }

  // Fetch all customers basic info with exclusion of the current customer
  @Action(FetchAllCustomersBasicInfo)
  public fetchAll(
    { getState, setState }: StateContext<CustomersStateModel>,
    { filters }: FetchAllCustomersBasicInfo
  ): Observable<Customer[]> {
    return this.customersService.getAllBasicInfo(filters).pipe(
      tap((freshCustomers: Customer[]) => {
        const state = getState();
        const nonCurrentlySelectedCustomers = freshCustomers.filter(
          (c) => c.id !== state.selectedCustomerId
        );

        // add or update
        nonCurrentlySelectedCustomers.forEach((customer) => {
          setState(
            patch<CustomersStateModel>({
              customers: iif<Customer[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (customers) => customers!.some((x) => x.id === customer.id),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                updateItem<Customer>((x) => x!.id === customer.id, customer),
                insertItem<Customer>(customer)
              ),
            })
          );
        });
        // remove
        const customerToRemove = state.customers.filter((c) =>
          freshCustomers.every((fc) => fc.id !== c.id)
        );
        customerToRemove.forEach((customer) => {
          setState(
            patch<CustomersStateModel>({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              customers: removeItem<Customer>((x) => x!.id === customer.id),
            })
          );
        });
      })
    );
  }

  @Action(FetchCurrentUserAllCustomersBasicInfo)
  public fetchCurrectUserCustomers({
    setState,
  }: StateContext<CustomersStateModel>): Observable<Customer[]> {
    return this.customersService.getCurrentUserAllBasicInfo().pipe(
      tap((customers: Customer[]) => {
        setState(
          patch<CustomersStateModel>({
            currentUserCustomers: customers,
          })
        );
      })
    );
  }

  @Action(FetchCustomer)
  public fetchSingle(
    { setState }: StateContext<CustomersStateModel>,
    { id }: FetchCustomer
  ): Observable<Customer> {
    return this.customersService.getCustomer(id).pipe(
      tap((customer: Customer) => {
        setState(
          patch<CustomersStateModel>({
            customers: iif<Customer[]>(
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              (customers) => customers!.some((x) => x.id === customer.id),
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              updateItem<Customer>((x) => x!.id === customer.id, customer),
              insertItem<Customer>(customer)
            ),
            selectedCustomerActions: customer.configuredActions,
            selectedCustomerMappings: customer.mappings,
          })
        );
      })
    );
  }

  @Action(UpdateCustomer)
  public update(
    { setState, dispatch }: StateContext<CustomersStateModel>,
    { payload }: UpdateCustomer
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.customersService.updateCustomer(payload).pipe(
      tap(() => {
        setState(
          patch({
            customerPluginConfigurations:
              new Array<CustomerPluginConfiguration>(),
          })
        );
        dispatch(new FetchCustomer(payload.id));
      })
    );
  }

  @Action(DeleteCustomer)
  public delete(
    { setState }: StateContext<CustomersStateModel>,
    { id }: DeleteCustomer
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.customersService.deleteCustomer(id).pipe(
      tap(() => {
        setState(
          patch({
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            customers: removeItem<Customer>((x) => x!.id === id),
            selectedCustomerId: '',
          })
        );
      })
    );
  }

  @Action(AddOrUpdateCustomerActionConfiguration)
  public addOrUpdateCustomerActionConfiguration(
    { setState }: StateContext<CustomersStateModel>,
    { payload }: AddOrUpdateCustomerActionConfiguration
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.customersService
      .addOrUpdateCustomerActionConfiguration(payload)
      .pipe(
        tap(() => {
          setState(
            patch({
              customerActionConfigurations: iif<CustomerActionConfiguration[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
                (customerConfigurations) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  customerConfigurations!.some(
                    (x) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      x!.customerId === payload.customerId &&
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      x!.actionId === payload.actionId
                  ),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
                removeItem<CustomerActionConfiguration>(
                  (x) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    x!.customerId === payload.customerId &&
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    x!.actionId === payload.actionId
                )
              ),
              selectedCustomerActions: insertItem<string>(payload.actionId),
            })
          );
        })
      );
  }

  @Action(DeleteCustomerActionConfiguration)
  public deleteCustomerActionConfiguration(
    { setState }: StateContext<CustomersStateModel>,
    { customerId, actionId }: DeleteCustomerActionConfiguration
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.customersService
      .deleteCustomerActionConfiguration(customerId, actionId)
      .pipe(
        tap(() => {
          setState(
            patch({
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, max-len
              customerActionConfigurations:
                removeItem<CustomerActionConfiguration>(
                  (x) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    x!.customerId === customerId && x!.actionId === actionId
                ),
              selectedCustomerActions: removeItem<string>(
                (x) => x === actionId
              ),
            })
          );
        })
      );
  }

  @Action(AddCustomer)
  public add(
    { setState }: StateContext<CustomersStateModel>,
    { payload }: AddCustomer
  ): Observable<string> {
    return this.customersService.addCustomer(payload).pipe(
      tap((customerId) => {
        payload.id = customerId;

        setState(
          patch({
            customers: append<Customer>([payload]),
            selectedCustomerId: payload.id,
          })
        );
      })
    );
  }

  @Action(AddCustomerMapping)
  public addMapping(
    { setState }: StateContext<CustomersStateModel>,
    { payload }: AddCustomerMapping
  ): void {
    setState(
      patch({
        selectedCustomerMappings: append<string>([payload]),
      })
    );
  }

  @Action(ImportCustomer)
  public import(
    { setState }: StateContext<CustomersStateModel>,
    { payload }: ImportCustomer
  ): Observable<string> {
    return this.customersService.importCustomer(payload).pipe(
      tap((customerId) => {
        setState(
          patch({
            selectedCustomerId: customerId,
            activePluginId: '',
            customerPluginConfigurations: iif<CustomerPluginConfiguration[]>(
              (pluginConfiguration) =>
                pluginConfiguration !== undefined &&
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                pluginConfiguration!.some((x) => x!.customerId === customerId),
              removeItem<CustomerPluginConfiguration>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (x) => x!.customerId === customerId
              )
            ),
          })
        );
      })
    );
  }

  @Action(SetActivePluginId)
  public setActivePluginId(
    { setState }: StateContext<CustomersStateModel>,
    { pluginId }: SetActivePluginId
  ): void {
    setState(
      patch({
        activePluginId: pluginId,
      })
    );
  }

  @Action(SetSelectedMessageType)
  public setSelectedMessageType(
    { setState }: StateContext<CustomersStateModel>,
    { messageType }: SetSelectedMessageType
  ): void {
    setState(
      patch({
        selectedMessageType: messageType,
      })
    );
  }

  @Action(SetCurrentCustomerId)
  public setCurrentCustomerId(
    { setState }: StateContext<CustomersStateModel>,
    { id }: SetCurrentCustomerId
  ): void {
    setState(
      patch({
        selectedCustomerId: id,
        activePluginId: '',
      })
    );
  }

  @Action(ClearCurrentCustomerId)
  public clearCurrentCustomerId({
    setState,
  }: StateContext<CustomersStateModel>): void {
    setState(
      patch({
        ...setState,
        selectedCustomerId: '',
        selectedMessageType: '',
        activePluginId: '',
      })
    );
  }

  @Action(UpdatePluginCustomerConfigurationFile)
  public updatePluginConfigurationFile(
    { setState }: StateContext<CustomersStateModel>,
    { pluginId, customerId, payload }: UpdatePluginCustomerConfigurationFile
  ): // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Observable<any> {
    return this.customersService
      .updatePluginCustomerConfigurationFile(pluginId, customerId, payload)
      .pipe(
        tap(() => {
          setState(
            patch<CustomersStateModel>({
              customerPluginConfigurations: iif<CustomerPluginConfiguration[]>(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (pluginConfiguration) =>
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  pluginConfiguration!.some(
                    (x) =>
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      x!.pluginId === pluginId && x!.customerId === customerId
                  ),
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                removeItem<CustomerPluginConfiguration>(
                  (x) =>
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    x!.pluginId === pluginId && x!.customerId === customerId
                )
              ),
            })
          );
        })
      );
  }
}
